import { CSSProperties, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getElementChildren } from '../selectors/elementChildrenSelector';
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { ImMap, ImmutableMap, isEmpty } from '../../../common/utils/immutableHelper';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { isLocationAttachedInline } from '../../../common/elements/utils/elementLocationUtils';
import { getColor, getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getElementColorHexValue } from '../../../common/colors/elementColorFormatUtil';
import { getLightDarkComboColors } from '../../../common/colors/colorConversionUtil';
import { getSelectedElementIds } from '../selection/selectedElementsSelector';
import Immutable from 'immutable';

type CssStyleProps = Record<string, string>;

/**
 * If this element has any inline attached comments, a CSS variable map is returned
 * containing the light/dark color variants for each inline comment.
 *
 * It might also add a text color variable if the dark color is too dark to use white text on.
 */
const makeSelectAttachedCommentStyles = (elementId: string) => {
    const memoisedGetElementChildren = getElementChildren();

    return createShallowSelector(
        (state: ImmutableMap) =>
            memoisedGetElementChildren(state, { elementId }) as unknown as ImMap<ImMNElement> | undefined,
        getSelectedElementIds,
        (
            children: ImMap<ImMNElement> | undefined,
            selectedElementIds: Immutable.List<string>,
        ): CSSProperties | undefined => {
            if (isEmpty(children)) return;

            // Imperative build (no reduce) for clarity & perf; only add entries when a valid colorHex exists
            const childColors: CssStyleProps = {};
            const childArr = children!.toArray();

            childArr.forEach((child) => {
                if (!isLocationAttachedInline(child)) return;

                const id = getElementId(child);
                const isSelected = selectedElementIds.contains(id);
                const colorHex = getElementColorHexValue(getColor(child));

                if (!colorHex && !isSelected) return;

                if (colorHex) {
                    const { lightColor, darkColor, whiteTextOnDarkAllowed } = getLightDarkComboColors(colorHex);

                    childColors[`--inline-comment-${id}-light-color`] = lightColor;
                    childColors[`--inline-comment-${id}-dark-color`] = darkColor;

                    if (!whiteTextOnDarkAllowed) {
                        childColors[`--inline-comment-${id}-text-color`] = 'var(--palette-grey-900)';
                    }
                }

                if (isSelected) {
                    childColors[`--inline-comment-${id}-border-width`] = '2px';
                    childColors[`--inline-comment-${id}-select-color`] = 'var(--inline-select-color)';
                }
            });

            return childColors as CSSProperties;
        },
    );
};

/**
 * This creates an appropriately memoised selector for the specified elementId - returning a map
 * of CSS properties to be applied to the element (or editor container).
 *
 * These properties are referenced by the InlineComment extension when rendering inline comments.
 */
export const useAttachedCommentStyles = (elementId: string) => {
    const selectAttachedCommentStyles = useMemo(() => makeSelectAttachedCommentStyles(elementId), [elementId]);
    return useSelector(selectAttachedCommentStyles);
};
